import { useState, useEffect, useMemo } from "react";
import moment from "moment";
import { companyToday, companyTodayStr, companyMonthStr } from "../helpers/companyTime";
import { useSelector, useDispatch } from "react-redux";
import { getCompanyUsers } from "../redux/company/actions";
import { UseAuth, UseIsDemo } from "./auth";

export const UseCompany = () => {
  const company = useSelector((state) => state.companyReducer.company);
  return company;
};

export const UseYetiBadguy = () => {
  const company = useSelector((state) => state.companyReducer.company);

  return company?.gameSettings?.badGuy;
};

export const UseSubscriptionLevel = () => {
  const subscriptionLevel = useSelector(
    (state) => state.companyReducer.company.subscription?.level || 0,
  );
  return subscriptionLevel;
};

export const UseCompanyLoading = () => {
  const isLoading = useSelector((state) => state.companyReducer.isLoading);
  // Wrap core state in useMemo so the state change takes effect on next render
  // This prevents some components to render based on incorrect states, and allow the correct states to be propagated
  // The incorrect-state bug happens like in the case when the app flashes the anauthorized page briefly after logging in
  const companyLoading = useMemo(() => isLoading, [isLoading]);
  return companyLoading;
};

export const UseIsCompanyUpToDate = () => {
  const updated = useSelector((state) => state.companyReducer.updated ?? false);
  return updated;
};

export const UseCompanyName = () => {
  const companyName = useSelector(
    (state) =>
      state.authReducer.userDetails?.companyName ||
      state.companyReducer.company.businessName,
  );
  return companyName;
};

export const UseCompanyLogo = () => {
  const isDemo = UseIsDemo();
  const logo = useSelector((state) =>
    isDemo
      ? state.authReducer.userDetails?.companyLogo
      : state.companyReducer.company.logo,
  );
  return logo;
};

export const UseCompanyIndustry = () => {
  const isDemo = UseIsDemo();
  const industry = useSelector((state) =>
    isDemo
      ? state.authReducer.userDetails?.industry
      : state.companyReducer.company.industry,
  );
  return industry;
};

export const UseCompanyColors = () => {
  const isDemo = UseIsDemo();
  const userDetails = useSelector((state) => state.authReducer.userDetails);
  const companyDetails = useSelector((state) => state.companyReducer.company);

  if (isDemo) {
    return {
      primaryColor: userDetails.primaryColor,
      secondaryColor: userDetails.secondaryColor,
    };
  } else {
    return {
      primaryColor: companyDetails.primaryColor,
      secondaryColor: companyDetails.secondaryColor,
    };
  }
};

export const UseCompanyUsersRefresh = () => {
  const authenticated = UseAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    if (authenticated) {
      dispatch(getCompanyUsers());
    }
  }, [dispatch, authenticated]);
};

export const UseUsersDictionary = () => {
  const companyUsersDict = useSelector(
    (state) => state.companyReducer.usersDictionary,
  );
  return companyUsersDict;
};

/**
 * 
 * @param {*} all include both workspaces and subworkspaces if true; else include main workspaces only
 * @returns array of workspace objects 
 */
export const UseCompanyWorkspaces = (all = false) => {
  const allWorkspaces = useSelector((state) => state.workspacesReducer.workspaces);
  const workspaces = useMemo(() => {
    if(all) {
      return allWorkspaces;
    } else {
      return allWorkspaces.filter((workspace) => !workspace.parentId);
    }
  }, [allWorkspaces, all]);

  return workspaces;
};

export const UseCompanySubWorkspaces = () => {
  const workspaces = useSelector((state) => state.workspacesReducer.workspaces);

  const subworkspaces = useMemo(() => {
    const subs = {};
    workspaces?.forEach((w) => {
      if (w.parentId) {
        (subs[w.parentId] ??= []).push(w);
      }
    });
    return subs;
  }, [workspaces]);

  return subworkspaces;
};

const EMPTY_WORKSPACE = {};

export const UseSelectedWorkspace = () => {
  const workspaces = useSelector(
    (state) => state.workspacesReducer.selectedWorkspace ?? EMPTY_WORKSPACE,
  );
  return workspaces;
};

export const UseUserIdList = () => {
  const companyUsersList = useSelector(
    (state) => state.companyReducer.usersList,
  );
  return companyUsersList;
};

export const UseUsersFullData = () => {
  const [companyUsers, setCompanyUsers] = useState([]);

  const companyUsersDict = useSelector(
    (state) => state.companyReducer.usersDictionary,
  );
  const companyUsersList = useSelector(
    (state) => state.companyReducer.usersList,
  );

  useEffect(() => {
    if (companyUsersList?.length) {
      const users = companyUsersList.map((userId) => ({
        ...companyUsersDict[userId],
        userId,
      }));
      setCompanyUsers(users);
    } else {
      setCompanyUsers([]);
    }
  }, [companyUsersList, companyUsersDict]);

  return companyUsers;
};

export const UseActivePlayers = () => {
  const [activePlayers, setActivePlayers] = useState([]);
  const scorers = useSelector((state) => state.kpiReducer.usersKpis);

  useEffect(() => {
    setActivePlayers(scorers?.map((p) => p.userId) ?? []);
  }, [scorers]);
  return activePlayers;
};

export const UseHolidays = (month = null) => {
  const allHolidays = useSelector((state) => state.companyReducer.holidays);
  const holidays = useMemo(() => {
    if (month) {
      const monthRef = moment(month).format("YYYY-MM");
      const monthHolidays =
        allHolidays
          ?.filter((item) => item.date.startsWith(monthRef))
          .map((item) => moment.utc(item.date).date()) || [];
      return monthHolidays;
    }
    return allHolidays;
  }, [allHolidays, month])

  return holidays;
};

export const UseCompanyWorkspaceUsers = (groupId) => {
  const allCompanyUsers = UseUsersFullData();

  const companyWorkspaceUsers = useMemo(() => {
    return (groupId)
      ? allCompanyUsers?.filter(user => user?.groupId === groupId)
      : allCompanyUsers
  }, [allCompanyUsers, groupId]);

  return companyWorkspaceUsers;
;}

/** Count business days in a month for Yeti target scaling. */
function yetiBusinessDayCounts(month, holidays, asOf = moment()) {
  const today = moment(asOf);
  const weekStart = moment(asOf).startOf("week");
  const lastWeekStart = moment(asOf).subtract(1, "week").startOf("week");
  const lastWeekEnd = weekStart.clone().subtract(1, "day");
  const dayCount = moment(month).daysInMonth();
  const workbreaks =
    holidays
      ?.filter((item) => item.date.startsWith(month))
      .map((item) => moment.utc(item.date).date()) || [];

  let yetiWeek = 0;
  let yetiMonth = 0;
  let yetiLastWeek = 0;
  let totalDays = 0;

  for (let d = 0; d < dayCount; d += 1) {
    if (workbreaks.indexOf(d + 1) < 0) {
      const t = moment(month).startOf("month").add(d, "days");
      if (t.day() > 0 && t.day() < 6) {
        if (t <= today) {
          yetiMonth += 1;
        }
        totalDays += 1;
      }
    }
  }

  /* This week is counted on its OWN calendar walk, like last week below, not
     from the month loop above: a week that starts in the previous month (Oct 1
     falls in the week of Sep 27) lost every day before the 1st, so the Yeti's
     "This week" pace read 1 elapsed business day instead of 4 for the first
     days of every month. A historic month keeps 0 — its week bucket is not a
     window anyone is looking at. */
  const weekTouchesMonth = weekStart.format("YYYY-MM") === month
    || today.format("YYYY-MM") === month;
  if (weekTouchesMonth) {
    const wk = weekStart.clone();
    while (wk <= today) {
      const monthKey = wk.format("YYYY-MM");
      const wkBreaks =
        holidays
          ?.filter((item) => item.date.startsWith(monthKey))
          .map((item) => moment.utc(item.date).date()) || [];
      if (wk.day() > 0 && wk.day() < 6 && wkBreaks.indexOf(wk.date()) < 0) {
        yetiWeek += 1;
      }
      wk.add(1, "day");
    }
  }

  const lw = lastWeekStart.clone();
  while (lw <= lastWeekEnd) {
    const monthKey = lw.format("YYYY-MM");
    const lwBreaks =
      holidays
        ?.filter((item) => item.date.startsWith(monthKey))
        .map((item) => moment.utc(item.date).date()) || [];
    if (lw.day() > 0 && lw.day() < 6 && lwBreaks.indexOf(lw.date()) < 0) {
      yetiLastWeek += 1;
    }
    lw.add(1, "day");
  }

  return { yetiWeek, yetiMonth, yetiLastWeek, totalDays };
}

/** Mon–Fri days in [from, to], skipping company holidays (same rule as Yeti MTD). */
function countYetiBusinessDaysInRange(from, to, holidays) {
  let n = 0;
  const d = moment(from).startOf("day");
  const end = moment(to).startOf("day");
  if (!d.isValid() || !end.isValid() || end.isBefore(d, "day")) return 0;
  while (d.isSameOrBefore(end, "day")) {
    const monthKey = d.format("YYYY-MM");
    const breaks =
      holidays
        ?.filter((item) => item.date.startsWith(monthKey))
        .map((item) => moment.utc(item.date).date()) || [];
    if (d.day() > 0 && d.day() < 6 && breaks.indexOf(d.date()) < 0) {
      n += 1;
    }
    d.add(1, "day");
  }
  return n;
}

/**
 * Month-to-date target for a KPI — same proration Yeti / workspace cards use.
 * Sum metrics: monthly target is spread across business days in the month.
 */
export function getKpiPeriodTarget(kpi, elapsedBusinessDays, totalBusinessDaysInMonth) {
  const baseTarget = Number(kpi?.minimum || kpi?.target) || 0;
  if (baseTarget <= 0) return 0;

  const totalDays = totalBusinessDaysInMonth > 0 ? totalBusinessDaysInMonth : 1;
  const dailyTarget =
    kpi.calc === "sum" && kpi.metricType !== "anchor"
      ? Math.round((baseTarget / totalDays) * 100) / 100
      : baseTarget;
  const isFullPeriod = kpi.wholeMonth || kpi.metricType === "anchor";
  return dailyTarget * (isFullPeriod ? 1 : elapsedBusinessDays);
}

/**
 * Windows whose Yeti pace must follow the resolved [from, to] via the `custom`
 * bucket — never MTD. week / lastWeek keep their named buckets (same formulas
 * as a custom range of those dates) so KOTM and UseTopPlayers stay aligned.
 */
export const windowUsesRangeYeti = (win) =>
  win === "custom"
  || win === "quarter"
  || win === "ytd"
  || win === "year";

/**
 * The month / as-of day / range buildYetiScoresForKpis must be handed to pace a
 * window. One definition, so useScopedMetrics and anything else that builds a
 * Yeti pace for a single metric (derived tiles) can never disagree about it.
 *
 * @param {string} win        resolved window id (month, lastMonth, quarter, ytd, …)
 * @param {moment.Moment} from  resolveWindow(win).from
 * @param {moment.Moment} to    resolveWindow(win).to
 */
export const yetiInputsForWindow = (win, from, to) => {
  let monthRef = companyMonthStr();
  if (win === "lastMonth") {
    monthRef = companyToday().subtract(1, "month").format("YYYY-MM");
  } else if (windowUsesRangeYeti(win)) {
    monthRef = from.format("YYYY-MM");
  }
  let asOf = companyToday();
  if (win === "lastMonth") {
    asOf = moment(monthRef, "YYYY-MM").endOf("month");
  } else if (windowUsesRangeYeti(win)) {
    asOf = moment.min(to.clone().endOf("day"), companyToday().endOf("day"));
  }
  const range = windowUsesRangeYeti(win)
    ? { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") }
    : null;
  return { monthRef, asOf, range };
};

/**
 * Which yeti period object to read for a board window.
 */
export const yetiBucketForWindow = (win) => {
  if (["today", "yesterday", "week", "lastWeek", "custom"].includes(win)) return win;
  if (windowUsesRangeYeti(win)) return "custom";
  return "month"; // month | lastMonth | …
};

/**
 * Build Yeti benchmark scores for a KPI list and month.
 * Shared by UseYetiStats and historic-month leaderboard enrichment.
 *
 * @param {{from?:string,to?:string}|null} range  When set, fills `custom` with
 *   progressive pace for business days in [from, to] (capped at asOf). Used for
 *   navbar Custom and for quarter/YTD/year so those are date-window paced too.
 */
export function buildYetiScoresForKpis(scoringKpis, month, holidays, asOf = companyToday(), range = null) {
  const kpis = Array.isArray(scoringKpis) ? scoringKpis : [];
  const { yetiWeek, yetiMonth, yetiLastWeek, totalDays } = yetiBusinessDayCounts(month, holidays, asOf);

  let customElapsed = 0;
  let customRateDays = totalDays;
  if (range?.from) {
    const fromM = moment(range.from, "YYYY-MM-DD").startOf("day");
    let toM = moment(range.to || range.from, "YYYY-MM-DD").startOf("day");
    const cap = moment(asOf).startOf("day");
    if (toM.isAfter(cap, "day")) toM = cap;
    customElapsed = countYetiBusinessDaysInRange(fromM, toM, holidays);
    /* Cross-month ranges: average each touched month's business-day count so
       daily rate isn't stuck on the start month alone. */
    const monthKeys = new Set();
    const walk = fromM.clone();
    while (walk.isSameOrBefore(toM, "day")) {
      monthKeys.add(walk.format("YYYY-MM"));
      walk.add(1, "day");
    }
    if (monthKeys.size > 1) {
      let sum = 0;
      monthKeys.forEach((mk) => {
        sum += yetiBusinessDayCounts(mk, holidays, moment(mk, "YYYY-MM").endOf("month")).totalDays;
      });
      customRateDays = Math.max(1, Math.round(sum / monthKeys.size));
    }
  }

  const yetiScore = {
    yesterday: { total: 0 },
    today: { total: 0 },
    week: { total: 0 },
    lastWeek: { total: 0 },
    month: { total: 0 },
    custom: { total: 0 },
    last31: { total: 0 },
    maxTotal: 0,
  };

  kpis.forEach((kpi) => {
    const target = kpi.minimum || kpi.target;
    const dailyTarget =
      kpi.calc === "sum" && kpi.metricType !== "anchor"
        ? Math.round((target / totalDays) * 100) / 100
        : target;

    const ptsScale = Number(kpi.pointScale) || 0;
    const ptsValue = Number(kpi.pointValue) || 0;
    // Legacy metrics (pre-pointValue docs, e.g. 2022's Closed Rate) encode
    // their rule as "pointScale = points per unit amount" — the convention the
    // server still scores players with (calcPoints: amount × pointScale).
    // Reading pointValue ÷ pointScale on those docs paid the Yeti 0 points
    // (0 ÷ 5000) on a metric players earned thousands from, so he ranked 3rd
    // while beating every amount on the board. Metrics with a pointValue use
    // the modern ratio; without one, a pointsRule pair wins, else legacy.
    const pointsRule = kpi.parameters?.pointsRule;
    let ptsPerAmt = ptsScale > 0 ? ptsValue / ptsScale : ptsValue;
    // Treat a missing OR zero pointValue as "not configured" — the two KPI-list
    // builders disagree (saga omits pointValue, useKpiData coerces it to 0), so
    // `== null` alone let 0 slip through and paid the Yeti 0 pts on legacy
    // pointScale-only metrics (Memberships, Sla Met).
    if (!kpi.pointValue) {
      if (pointsRule && Number(pointsRule.metricAmount) > 0) {
        // Gamified-in-place metric: pointsRule is authoritative when the doc
        // has no pointValue (pointScale still carries the legacy server rate,
        // so the pointValue ÷ pointScale ratio would be wrong here).
        ptsPerAmt = Number(pointsRule.pointsEarned || 0) / Number(pointsRule.metricAmount);
      } else if (!pointsRule) {
        ptsPerAmt = ptsScale;
      }
    }

    const isAnchor = kpi.metricType === "anchor";
    const isFullPeriod = kpi.wholeMonth || isAnchor;
    const anchorBenchmark = Number(kpi?.optional?.benchmarkPoint) || 0;
    const ptsAtAnchorAmt = (amtAtAnchor) =>
      isAnchor ? anchorBenchmark : amtAtAnchor * ptsPerAmt;

    const todayAmt = dailyTarget;
    const todayPts = ptsAtAnchorAmt(todayAmt);
    /* Store amt for the donut/$ bars; pts 0 on anchors so any path that sums
       per-KPI pts (or recomputes totals) cannot re-inflate the day rank. */
    const dayPts = (isAnchor || kpi.wholeMonth) ? 0 : todayPts;
    yetiScore.today[kpi._id] = { pts: dayPts, amt: todayAmt };
    yetiScore.yesterday[kpi._id] = { pts: dayPts, amt: todayAmt };
    /* Anchors (Closed Rate, AVG Ticket) must NOT pad Today/Yesterday rank totals
       — whole-period benchmarks (~1000 pts each) made The Yeti stick at ~2301
       while reps showed single-digit day scores on the homepage leaderboard. */
    if (!isAnchor && !kpi.wholeMonth) {
      yetiScore.today.total += dayPts;
      yetiScore.yesterday.total += dayPts;
    }

    const weekAmt = getKpiPeriodTarget(kpi, yetiWeek, totalDays);
    const weekPts = ptsAtAnchorAmt(weekAmt);
    /* Week / Last week: same as Today & custom — progressive pace only in the
       rank total. Anchors keep amt for beat bars. Otherwise "Last week" Yeti
       sat ~4.8k while custom Aug 31–Sep 6 (same Mon–Fri) sat ~1.5k. */
    const weekCellPts = (isAnchor || kpi.wholeMonth) ? 0 : weekPts;
    yetiScore.week[kpi._id] = { pts: weekCellPts, amt: weekAmt };
    if (!isAnchor && !kpi.wholeMonth) {
      yetiScore.week.total += weekCellPts;
    }

    /* Must divide by month business days (`totalDays`), not `yetiLastWeek`.
       Passing last-week length as the denominator made dailyTarget = month/5
       and lastWeekAmt = full month target — Yeti looked ~3× a real week. */
    const lastWeekAmt = getKpiPeriodTarget(kpi, yetiLastWeek, totalDays);
    const lastWeekPts = ptsAtAnchorAmt(lastWeekAmt);
    const lastWeekCellPts = (isAnchor || kpi.wholeMonth) ? 0 : lastWeekPts;
    yetiScore.lastWeek[kpi._id] = { pts: lastWeekCellPts, amt: lastWeekAmt };
    if (!isAnchor && !kpi.wholeMonth) {
      yetiScore.lastWeek.total += lastWeekCellPts;
    }

    const monthAmt = getKpiPeriodTarget(kpi, yetiMonth, totalDays);
    const monthPts = ptsAtAnchorAmt(monthAmt);
    yetiScore.month[kpi._id] = { pts: monthPts, amt: monthAmt };
    yetiScore.month.total += monthPts;

    /* Custom / quarter / YTD / year: pace to business days in the selected
       [from, to] only — never month-to-date. Anchors keep amt for beat bars. */
    if (range?.from) {
      const elapsed = Math.max(customElapsed, 1);
      const customAmt = getKpiPeriodTarget(kpi, elapsed, customRateDays);
      const customPts = ptsAtAnchorAmt(customAmt);
      const cellPts = (isAnchor || kpi.wholeMonth) ? 0 : customPts;
      yetiScore.custom[kpi._id] = { pts: cellPts, amt: customAmt };
      if (!isAnchor && !kpi.wholeMonth) {
        yetiScore.custom.total += cellPts;
      }
    }

    const maxAmt = dailyTarget * (isFullPeriod ? 1 : totalDays);
    yetiScore.maxTotal += ptsAtAnchorAmt(maxAmt);
  });

  return { ...yetiScore, totalDays };
}

/**
 * The metrics the Yeti is benchmarked on: the ones that are switched on.
 *
 * `kpiReducer.groupKpis` is not that list — the saga keeps recently-disabled
 * metrics in it (history still renders them), the hook drops them, so which one
 * you got depended on the last refresh. A disabled metric still carries its
 * target and pointValue, so the Yeti was paid a full month of benchmark on it
 * (a disabled "Technician Revenue Per Hour", 100 × 22 days × 10 pts = 22,000)
 * while players, who never enter it, earned ~1. Last Month's Yeti read 28,430
 * against a real ~5,400. The leaderboard table, dept metrics and scoped
 * metrics already hand buildYetiScoresForKpis enabled metrics only.
 */
export const yetiScoringKpis = (kpis) =>
  (Array.isArray(kpis) ? kpis : []).filter((k) => k && !k.disabled);

/** Merge supplemental Yeti KPI entries into an existing Yeti score object. */
export function mergeSupplementalYetiScores(baseYeti, supplementalYeti) {
  if (!baseYeti || !supplementalYeti) return baseYeti;

  const merged = { ...baseYeti };
  ["today", "yesterday", "week", "lastWeek", "month", "custom"].forEach((period) => {
    const basePeriod = baseYeti[period];
    const extraPeriod = supplementalYeti[period];
    if (!basePeriod || !extraPeriod) return;

    const nextPeriod = { ...basePeriod, total: basePeriod.total || 0 };
    Object.keys(extraPeriod).forEach((key) => {
      if (key === "total") return;
      if (nextPeriod[key]) return;
      nextPeriod[key] = extraPeriod[key];
      nextPeriod.total += extraPeriod[key]?.pts || 0;
    });
    merged[period] = nextPeriod;
  });

  merged.maxTotal = (merged.maxTotal || 0) + (supplementalYeti.maxTotal || 0);
  return merged;
}

export const UseYetiStats = (liveMode = true) => {
  const [stats, setStats] = useState(null);
  const holidays = useSelector((state) => state.companyReducer.holidays);
  const kpis = useSelector((state) => state.kpiReducer.groupKpis);
  const monthRef = useSelector((state) => state.kpiReducer.monthRef);
  const currentDay = companyTodayStr();

  useEffect(() => {
    const scoringKpis = yetiScoringKpis(kpis);
    if (scoringKpis.length) {
      const month = monthRef && !liveMode ? monthRef : companyMonthStr();
      setStats(buildYetiScoresForKpis(scoringKpis, month, holidays));
    } else {
      setStats(null);
    }
  }, [kpis, holidays, liveMode, monthRef, currentDay]);

  return stats;
};